// components/auth/national-day-theme.tsx
// Saudi National Day theming (time-boxed, engineer request 2026-09-21:
// "within two days", the 2026 anniversary falls on 23/09). Self-expiring
// by date rather than a flag someone has to remember to turn off later:
// active only inside a short window around the day itself, so this file
// can stay in the repo year to year without ever needing a manual on/off
// toggle -- update NATIONAL_DAY below for a future year if this gets reused.
//
// Deliberately a plain function, NOT a hook (no "use" prefix, no "use
// client" here): it does nothing but compare Date objects, no React state
// or effects involved, so it's safe to call from both Server and Client
// Components. A real bug, confirmed live 2026-09-21: this used to be named
// useIsNationalDayWindow and required "use client", which crashed
// welcome-hero.tsx and login-hero-panel.tsx (both plain Server Components)
// the moment they called it -- "Attempted to call ... from the server but
// ... is on the client." Every caller now just calls a normal function.

// Live feedback, 2026-09-22: "we need to apply green now" -- moved up to
// match the banner's own start (today, not tomorrow). Saudi Arabia has no
// DST, always UTC+3, so these are fixed-offset instants rather than plain
// local dates -- through the end of Sunday 2026-09-27 KSA (i.e. up to, not
// including, Monday 00:00 KSA).
const WINDOW_START = new Date("2026-09-22T15:00:00+03:00");
const WINDOW_END = new Date("2026-09-28T00:00:00+03:00");

// Live feedback, 2026-09-21: "just the top Happy Saudi National holiday...
// needs to show 3pm Sep 22, till Sunday" -- the top photo banner
// (components/national-day-banner.tsx) starts a full day earlier than
// everything else (login page, sidebar, buttons, etc, which stay on the
// window above). Same end point, Sunday 2026-09-27 KSA.
const BANNER_WINDOW_START = new Date("2026-09-22T15:00:00+03:00");
const BANNER_WINDOW_END = WINDOW_END;

export function isNationalDayWindowActive(): boolean {
  const now = new Date();
  return now >= WINDOW_START && now < WINDOW_END;
}

export function isNationalDayBannerWindowActive(): boolean {
  const now = new Date();
  return now >= BANNER_WINDOW_START && now < BANNER_WINDOW_END;
}

// Shared with components/national-day-banner.tsx and login-hero-panel.tsx,
// live feedback 2026-09-22: "I want it to appear also in the public
// endpoint" -- the same real GEA campaign photography (pre-processed via
// sharp, see national-day-banner.tsx's own note) shows everywhere this
// theme touches a photo, not just the dashboard banner.
export const NATIONAL_DAY_BANNER_IMAGES = [
  "/images/events/national-day-banner-snd2.webp",
  "/images/events/national-day-banner-snd6.webp",
  "/images/events/national-day-banner-snd7.webp",
  "/images/events/national-day-banner-snd8.webp",
  "/images/events/national-day-banner-snd9.webp",
];

// Deterministic per key (a pathname, or any other stable string) rather
// than Math.random on every render, so the image doesn't flicker/swap on
// unrelated re-renders while still varying across different pages/panels.
export function pickNationalDayBannerImage(key: string): string {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  const index = Math.abs(hash) % NATIONAL_DAY_BANNER_IMAGES.length;
  return NATIONAL_DAY_BANNER_IMAGES[index];
}
